import type { Theme, ThemeColors } from "./types";

// ─── Module-level state ───────────────────────────────────────────────────────

/** Name of the last theme that was fully applied, used for fast deduplication. */
let lastAppliedThemeName: string | null = null;

/** Last applied dark-mode state — skip classList mutation when unchanged. */
let lastAppliedIsDark: boolean | null = null;

/**
 * Flat snapshot of the last applied color values, keyed by CSS property name.
 * Used for delta updates: only properties whose values actually changed get a
 * new `setProperty` call.
 */
const lastAppliedProps: Record<string, string> = {};

/** Cached reference to <meta name="theme-color">. */
let themeColorMetaEl: HTMLMetaElement | null = null;

// ─── Static property map ──────────────────────────────────────────────────────

/**
 * Ordered list of [cssPropertyName, colorsKey] pairs for all scalar color
 * properties.  Defined once at module load — zero allocation on each call.
 */
const SCALAR_PROPS: ReadonlyArray<[string, keyof ThemeColors]> = [
  ["--background", "background"],
  ["--foreground", "foreground"],
  ["--card", "card"],
  ["--card-foreground", "cardForeground"],
  ["--popover", "popover"],
  ["--popover-foreground", "popoverForeground"],
  ["--primary", "primary"],
  ["--primary-foreground", "primaryForeground"],
  ["--secondary", "secondary"],
  ["--secondary-foreground", "secondaryForeground"],
  ["--muted", "muted"],
  ["--muted-foreground", "mutedForeground"],
  ["--accent", "accent"],
  ["--accent-foreground", "accentForeground"],
  ["--destructive", "destructive"],
  ["--border", "border"],
  ["--input", "input"],
  ["--ring", "ring"],
  ["--sidebar", "sidebar"],
  ["--sidebar-foreground", "sidebarForeground"],
  ["--sidebar-primary", "sidebarPrimary"],
  ["--sidebar-primary-foreground", "sidebarPrimaryForeground"],
  ["--sidebar-accent", "sidebarAccent"],
  ["--sidebar-accent-foreground", "sidebarAccentForeground"],
  ["--sidebar-border", "sidebarBorder"],
  ["--sidebar-ring", "sidebarRing"],
] as const;

/** CSS property names for the five chart color slots. */
const CHART_PROPS = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5"] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getOrCreateThemeColorMetaEl(): HTMLMetaElement {
  if (themeColorMetaEl) return themeColorMetaEl;

  const existing = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (existing) {
    themeColorMetaEl = existing;
    return existing;
  }

  const meta = document.createElement("meta");
  meta.setAttribute("name", "theme-color");
  document.head.appendChild(meta);
  themeColorMetaEl = meta;
  return meta;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Apply a theme to the document using targeted `style.setProperty()` calls on
 * `<html>`, updating **only the properties that actually changed** compared to
 * the previously applied theme (delta update).
 *
 * ### Why delta setProperty instead of a full <style> textContent swap
 *
 * Replacing `<style>.textContent` forces the browser to re-parse the entire
 * CSS text and invalidate *all* elements' styles in one pass.
 *
 * `element.style.setProperty()` on CSS custom properties is cheaper in two
 * ways:
 *   1. **No parse cost** — values are strings, no CSS grammar involved.
 *   2. **Selective invalidation** — the browser only re-styles elements that
 *      actually inherit the changed property, not the whole tree.
 *
 * Keeping a snapshot of the last applied values and skipping unchanged
 * properties reduces the number of setProperty calls from 28 down to the
 * handful that genuinely differ between two adjacent themes, which is the
 * common case during rapid hover-preview in ThemeSelector.
 *
 * Inline styles also have the highest cascade priority, so they naturally
 * override any `:root { }` block in `globals.css` without relying on
 * `<style>` element insertion order in `<head>`.
 *
 * ### Dark-class optimisation
 *
 * `classList.add/remove("dark")` triggers a full selector-match recalculation
 * for every rule that uses `:is(.dark *)` or `.dark` descendant selectors.
 * Skipping the mutation when the light/dark mode has not actually changed
 * eliminates that recalc entirely when previewing themes within the same mode.
 */
export const applyThemeToDocument = (theme: Theme): void => {
  if (typeof document === "undefined") return;

  // Fast path: nothing changed at all.
  if (lastAppliedThemeName === theme.name) return;
  lastAppliedThemeName = theme.name;

  const root = document.documentElement;
  const { colors } = theme;
  const s = root.style;

  // ── Dark class — only mutate when mode actually flips ───────────────────
  if (lastAppliedIsDark !== theme.isDark) {
    if (theme.isDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    lastAppliedIsDark = theme.isDark;
  }

  // ── Scalar properties — delta update ────────────────────────────────────
  for (let i = 0; i < SCALAR_PROPS.length; i++) {
    const [prop, key] = SCALAR_PROPS[i] as [string, keyof ThemeColors];
    const value = colors[key] as string;
    if (lastAppliedProps[prop] !== value) {
      s.setProperty(prop, value);
      lastAppliedProps[prop] = value;
    }
  }

  // ── Chart colors (array) — delta update ─────────────────────────────────
  for (let i = 0; i < CHART_PROPS.length; i++) {
    const prop = CHART_PROPS[i] as string;
    const value = colors.chartColors[i] as string;
    if (lastAppliedProps[prop] !== value) {
      s.setProperty(prop, value);
      lastAppliedProps[prop] = value;
    }
  }

  // ── theme-color meta ─────────────────────────────────────────────────────
  // Keep the browser chrome (e.g. Android address bar) in sync.
  getOrCreateThemeColorMetaEl().setAttribute("content", colors.background);
};
